import { useEffect, useRef, useState, useMemo, useCallback, useContext } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import { Paginator } from "primereact/paginator";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { BreadCrumb } from "primereact/breadcrumb";
import { ProgressSpinner } from "primereact/progressspinner";
import { Menu } from "primereact/menu";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from 'jspdf-autotable';

import { getTranslations, createTranslation, updateTranslation } from "../../api/translationApi";
import { getProducts } from "../../api/productApi";
import { getLanguages } from "../../api/languageApi";
import { getCountries } from "../../api/countryApi";
import TranslationForms from "./TranslationForms";
import { AuthContext } from "../../Context/AuthContext";

export default function Translation() {
  const toast = useRef(null);
  const { auth } = useContext(AuthContext);

  const [products, setProducts] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [countries, setCountries] = useState([]);
  const [translations, setTranslations] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);
  const [showForm, setShowForm] = useState(false);
  const [formType, setFormType] = useState(null);
  const [selectedSource, setSelectedSource] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const menu = useRef(null);

  const items = [{ label: "TRANSLATION" }];
  const home = { icon: "pi pi-home" };

  const loadTranslations = useCallback(async () => {
    if (!selectedProduct) return;
    setIsLoading(true);
    try {
      const res = await getTranslations(auth.token, {
        product_id: selectedProduct ?? undefined,
        country_id: selectedCountry ?? undefined,
      });
      const sources = res.data.data || [];
      const flat = [];
      sources.forEach(source => {
        if (!source.Translations || source.Translations.length === 0) {
          flat.push({
            id: null,
            source_id: source.id,
            Source: { key_id: source.key_id, is_enabled: source.is_enabled },
            translation: null,
            Language: null,
          });
        } else {
          source.Translations.forEach(t => {
            flat.push({
              id: t.id,
              source_id: source.id,
              Source: { key_id: source.key_id, is_enabled: source.is_enabled },
              translation: t.translation,
              language_id: t.language_id,
              Language: t.Language,
            });
          });
        }
      });
      setTranslations(flat);
    } catch (err) {
      console.error(err);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: err.response?.data?.message || "Failed to load translations",
      });
    } finally {
      setIsLoading(false);
    }
  }, [selectedProduct, selectedCountry, auth.token]);

  const loadInitialData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [pRes, lRes, cRes] = await Promise.all([
        getProducts(auth.token),
        getLanguages(auth.token),
        getCountries(auth.token)
      ]);
      setProducts(pRes.data.data.filter(p => p.is_enabled));
      setLanguages(lRes.data.data.filter(l => l.is_enabled));
      setCountries(cRes.data.data.filter(c => c.is_enabled));
      await loadTranslations();
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to load initial data" });
    } finally {
      setIsLoading(false);
    }
  }, [auth.token, loadTranslations]);

  useEffect(() => { loadInitialData(); }, [loadInitialData]);
  useEffect(() => { loadTranslations(); }, [loadTranslations]);

  const allowedLanguages = useMemo(() => {
    if (!selectedCountry) return languages;
    const country = countries.find(c => c.id === selectedCountry);
    if (!country?.Languages) return languages;
    const codes = country.Languages.map(l => l?.code).filter(Boolean);
    return languages.filter(l => codes.includes(l?.code));
  }, [selectedCountry, countries, languages]);

  const tableData = useMemo(() => {
    const map = {};
    translations.forEach(t => {
      if (!t.Source?.is_enabled) return;
      const sourceKey = t.Source.key_id;
      if (!map[sourceKey]) map[sourceKey] = { source: sourceKey, source_id: t.source_id, source_enabled: true };
      if (t.Language?.code) map[sourceKey][t.Language.code] = { id: t.id, text: t.translation, language_id: t.language_id, is_enabled: t.is_enabled };
    });
    Object.values(map).forEach(row => {
      allowedLanguages.forEach(lang => {
        if (!lang?.code) return;
        if (!row[lang.code]) row[lang.code] = { id: null, text: "", language_id: lang.id, is_enabled: true };
      });
    });
    return Object.values(map);
  }, [translations, allowedLanguages]);

  const filteredTableData = useMemo(() => {
    if (!selectedSource) return tableData;
    return tableData.filter(row => row.source === selectedSource);
  }, [tableData, selectedSource]);

  const textEditor = (options) => {
    const value = options.value?.text ?? "";
    return (
      <InputText
        value={value}
        autoFocus
        onChange={(e) =>
          options.editorCallback({
            id: options.value?.id ?? null,
            language_id: options.value?.language_id ?? null,
            is_enabled: options.value?.is_enabled ?? true,
            text: e.target.value
          })
        }
      />
    );
  };

  const onCellEditComplete = async ({ rowData, field, newValue }) => {
    const text = newValue === "" ? null : (typeof newValue === "string" ? newValue : newValue?.text);
    const lang = languages.find(l => l.code === field);
    if (!lang) return;
    if (!rowData.source_id) {
      toast.current?.show({ severity: "warn", summary: "Missing Source", detail: "Cannot update translation: row has no source_id" });
      return;
    }
    const existing = rowData[field];
    const payload = { product_id: selectedProduct, source_id: rowData.source_id, language_id: lang.id, translation: text };
    confirmDialog({
      message: "Update this translation?",
      header: "Confirm",
      icon: "pi pi-exclamation-triangle",
      accept: async () => {
        setIsLoading(true);
        try {
          let savedTranslation;
          if (existing?.id) {
            const res = await updateTranslation(existing.id, payload, auth.token);
            savedTranslation = res.data.data || res.data;
          } else if (text) {
            const res = await createTranslation(payload, auth.token);
            savedTranslation = res.data.data || res.data;
          } else return;
          setTranslations(prev => {
            if (existing?.id) return prev.map(t => t.id === existing.id ? savedTranslation : t);
            else return [...prev, { ...savedTranslation, Source: { key_id: rowData.source, is_enabled: true } }];
          });
          toast.current?.show({ severity: "success", summary: "Saved", detail: "Translation updated" });
        } catch (err) {
          console.error(err.response?.data || err);
          toast.current?.show({ severity: "error", summary: "Error", detail: err.response?.data?.message || err.message || "Save failed" });
        } finally { setIsLoading(false); }
      }
    });
  };

  const sourceFilterElement = () => {
    const sources = [...new Set(tableData.map(row => row.source))];
    return (
      <div className="p-inputgroup">
        <Dropdown
          value={selectedSource}
          options={[{ label: "All", value: "" }, ...sources.map(source => ({ label: source, value: source }))]}
          onChange={e => setSelectedSource(e.value)}
          editable
          panelStyle={{ maxWidth: '200px' }}
          placeholder="Select Source"
          showClear
        />
      </div>
    );
  };

  const exportJSON = () => {
    const data = selectedRows.length ? selectedRows : filteredTableData;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "translations.json";
    link.click();
  };

  const exportExcel = () => {
    const data = selectedRows.length ? selectedRows : filteredTableData;
    const sheetData = data.map(d => {
      const row = { Source: d.source };
      allowedLanguages.forEach(l => row[l.code] = d[l.code]?.text || "");
      return row;
    });
    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Translations");
    XLSX.writeFile(workbook, "translations.xlsx");
  };

  const exportPDF = () => {
    const data = selectedRows.length ? selectedRows : filteredTableData;
    if (data.some(d => d.source_enabled === false)) {
      toast.current?.show({
        severity: "warn",
        summary: "Action Forbidden",
        detail: "Disabled sources cannot be downloaded."
      });
      return;
    }
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text("Translations", 14, 20);
    const head = [["Source", ...allowedLanguages.map(l => l.name)]];
    const body = data.map(d => [d.source, ...allowedLanguages.map(l => d[l.code]?.text || "")]);
    autoTable(doc, {
      head,
      body,
      startY: 30,
      theme: "grid",
      headStyles: { fillColor: [252, 95, 28] }
    });
    doc.save("translations.pdf");
  };

  
  const menuItems = [
    {
      label: "Download JSON Format",
      icon: "pi pi-file",
      command: () => {
        setTimeout(() => {
          const sampleData = [
            { product: "ProductName", source: "source_key", language: "en", translation: "Sample translation" }
          ];
          const blob = new Blob([JSON.stringify(sampleData, null, 2)], { type: "application/json" });
          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.download = "translation_format.json";
          link.click();
        }, 0);
      }
    },
    {
      label: "Download Excel Format",
      icon: "pi pi-file-excel",
      command: () => {
        setTimeout(() => {
          const sampleData = [
            { product: "ProductName", source: "source_key", language: "en", translation: "Sample translation" }
          ];
          const ws = XLSX.utils.json_to_sheet(sampleData);
          const wb = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(wb, ws, "Template");
          XLSX.writeFile(wb, "translation_format.xlsx");
        }, 0);
      }
    }
  ];

  const handleUploadTranslationClick = () => { setFormType("upload"); setShowForm(true); };

  return (
    <div className="p-4" style={{ position: 'relative' }}>
      {isLoading && (
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(255,255,255,0.8)', zIndex: 10, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <ProgressSpinner />
        </div>
      )}
      <Toast ref={toast} />
      <ConfirmDialog />
      <h2 className="mb-1 text-3xl font-bold" style={{ color: "#FC5F1CFF" }}>TRANSLATION</h2>
      <div className="gap-2 mb-4">
        <BreadCrumb model={items} home={home} style={{ color: "#FC5F1CFF", background: "transparent", border: "none" }} />
      </div>
      
      <DataTable
        value={filteredTableData.slice(first, first + rows)}
        selection={selectedRows}
        onSelectionChange={e => setSelectedRows(e.value)}
        dataKey="source"
        selectionMode="multiple"
        editMode="cell"
        showGridlines
        rowClassName={rowData => !Object.values(rowData).every(v => v?.is_enabled !== false) ? "opacity-50" : ""}
        header={
          <div className="flex justify-content-end gap-3 items-center">
            <Dropdown
              value={selectedProduct}
              options={products.map(p => ({ label: p.name, value: p.id }))}
              placeholder="Select Product"
              showClear
              onChange={e => setSelectedProduct(e.value)}
            />
            <Dropdown
              value={selectedCountry}
              options={countries.map(c => ({ label: c.name, value: c.id }))}
              placeholder="Select Country"
              showClear
              onChange={e => setSelectedCountry(e.value)}
            />
            <Button 
              icon="pi pi-upload" 
              rounded 
              tooltip="Upload"
              style={{ backgroundColor: "#FC5F1CFF", border: "none", color: "#fff" }} 
              onClick={handleUploadTranslationClick} 
            />
            <Button 
              icon="pi pi-file" 
              rounded 
              tooltip="json"
              style={{ backgroundColor: "#FC5F1CFF", border: "none", color: "#fff" }} 
              onClick={exportJSON} 
            />
            <Button 
              icon="pi pi-file-excel" 
              rounded 
              tooltip="xlsx"
              style={{ backgroundColor: "#FC5F1CFF", border: "none", color: "#fff" }} 
              onClick={exportExcel} 
            />
            <Button 
              icon="pi pi-file-pdf" 
              rounded 
              tooltip="pdf"
              style={{ backgroundColor: "#FC5F1CFF", border: "none", color: "#fff" }} 
              onClick={exportPDF} 
            />
            <Menu model={menuItems} popup ref={menu} />
            <Button 
              icon="pi pi-ellipsis-v" 
              rounded 
              tooltip="Template"
              style={{ background: "#FC5F1CFF", border: "none", color: "#fff" }} 
              onClick={e => menu.current.toggle(e)} 
            />
          </div>
        }
        
        footer={
          <Paginator
            first={first}
            rows={rows}
            totalRecords={filteredTableData.length}
            rowsPerPageOptions={[10, 20, 30]}
            onPageChange={e => { setFirst(e.first); setRows(e.rows); }}
          />
        }
        
      >
        <Column selectionMode="multiple" style={{ width: "3rem" }} />
        <Column field="source" header="Source" filter filterElement={sourceFilterElement} />
        {allowedLanguages.map(lang => (
          <Column
            key={lang.code}
            field={lang.code}
            header={lang.name}
            body={row => row[lang.code]?.text || ""}
            editor={textEditor}
            onCellEditComplete={onCellEditComplete}
          />
        ))}
      </DataTable>

      <TranslationForms
        visible={showForm}
        uploadMode={formType === "upload"}
        onHide={() => setShowForm(false)}
        onSaved={() => {
          setSelectedSource("");
          loadTranslations();
          setShowForm(false);
        }}
      />
    </div>
  );
}
